import { clearClientAuthStorage } from './clientAuthStorage';
import { securityService } from './securityService';

export interface AuthenticatedFetchOptions extends RequestInit {
  /** Si true, no redirige a /login ante 401 tras agotar refresh. */
  skipAuthRedirect?: boolean;
}

function redirectToLogin(): void {
  if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
}

function buildHeaders(token: string, extra?: HeadersInit): HeadersInit {
  const currentCenterId = localStorage.getItem('currentCenterId');
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    ...(currentCenterId ? { 'X-Center-Id': currentCenterId } : {}),
    ...extra,
  };
}

/**
 * fetch autenticado con refresh proactivo y un reintento ante 401.
 */
export async function authenticatedFetch(
  url: string,
  options: AuthenticatedFetchOptions = {},
): Promise<Response> {
  const { skipAuthRedirect, headers, ...rest } = options;

  let token = await securityService.getValidAccessToken();
  if (!token) {
    clearClientAuthStorage();
    if (!skipAuthRedirect) redirectToLogin();
    throw new Error('Sesión expirada');
  }

  let response = await fetch(url, {
    ...rest,
    headers: buildHeaders(token, headers),
  });

  if (response.status === 401) {
    const refreshed = await securityService.refreshSession();
    if (refreshed) {
      token = await securityService.getValidAccessToken();
      if (token) {
        response = await fetch(url, {
          ...rest,
          headers: buildHeaders(token, headers),
        });
      }
    }
  }

  if (response.status === 401) {
    clearClientAuthStorage();
    if (!skipAuthRedirect) redirectToLogin();
    throw new Error('Token expirado');
  }

  return response;
}

/** Parsea JSON de una Response OK; maneja 204 y cuerpo vacío. */
export async function parseAuthenticatedJson<T = unknown>(response: Response): Promise<T | null> {
  if (response.status === 204) return null;

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      response.status === 403
        ? `403: Acceso denegado. ${errorText}`
        : `Error ${response.status}: ${errorText}`,
    );
  }

  const text = await response.text();
  if (!text?.trim()) return null;
  try {
    return JSON.parse(text) as T;
  } catch (err) {
    const contentType = response.headers.get('content-type');
    if (contentType?.includes('application/json')) throw err;
    return text as unknown as T;
  }
}
